import { useMemo } from 'react';
import { Typography, Box, Paper, Button } from '@mui/material';
import { format } from 'date-fns';
import BoxComment from '@/components/BoxComment';
import Comment from '@/components/Comment';
import CommentInputBox from '@/components/CommentInputBox';
import ContainerTitle from '@/components/general/ContainerTitle';
import QuestionItem from '@/components/QuestionItem';
import useProfile from '@/hooks/useProfileV2';
import { AvailableRoles } from '@/types';
import { Feedback, User, Answer, UserCheckin, DueDate, AnswerTypes } from '@/types/api';
import { defaultCheckinDateFormat } from '@/utils/checkins';
import styles from './UnreviewedCheckinDetails.styles';

export type UnreviewedCheckinDetailsType = Pick<UserCheckin, 'answeredAt'> & {
  answers: Answer[];
  dueDate?: DueDate;
  isCheckinReviewed: boolean;
  checkinId: string;
  handlePostFeedback: (comment?: string) => void;
  handleMarkAsReviewed: () => void;
  user?: User;
  feedbacks?: Feedback[];
  refetchCheckin: () => void;
  reviewButtonLabel?: string;
};

const UnreviewedCheckinDetails = function ({
  dueDate,
  answeredAt,
  answers,
  isCheckinReviewed,
  checkinId,
  handlePostFeedback,
  handleMarkAsReviewed,
  user,
  refetchCheckin,
  feedbacks,
  reviewButtonLabel = 'Mark as reviewed',
}: UnreviewedCheckinDetailsType): JSX.Element {
  const { profile } = useProfile();

  const isUserAllowedToGiveFeedback = useMemo(
    () => profile.roles?.find((role) => role.value === AvailableRoles.PEOPLE_ADMIN),
    [profile]
  );

  const renderPriorities = useMemo(() => {
    const priorities = answers.filter((answer) => {
      return answer.question.answerType === AnswerTypes.PRIORITY;
    });

    return priorities.map((answer, index) => (
      <QuestionItem
        {...answer}
        isPriority
        withoutLabel={index !== 0}
        refetchCheckin={refetchCheckin}
        reactions={answer.reactions ?? []}
        key={answer.question.id}
        label={answer.submittedQuestionLabel}
        description={answer.question.description}
        answerType={answer.question.answerType}
        questionOptions={answer.question.options}
        answer={answer.response}
        answerId={answer.id}
        comments={answer.comments}
        checkinId={checkinId}
      />
    ));
  }, [answers, checkinId, refetchCheckin]);

  const renderQuestions = useMemo(() => {
    const answersWithoutPriorities = answers.filter((answer) => {
      return answer.question.answerType !== AnswerTypes.PRIORITY;
    });
    return answersWithoutPriorities.map((answer) => (
      <QuestionItem
        {...answer}
        refetchCheckin={refetchCheckin}
        reactions={answer.reactions ?? []}
        key={answer.question.id}
        label={answer.submittedQuestionLabel}
        description={answer.question.description}
        answerType={answer.question.answerType}
        questionOptions={answer.question.options}
        answer={answer.response}
        answerId={answer.id}
        comments={answer.comments}
        checkinId={checkinId}
      />
    ));
  }, [answers, checkinId, refetchCheckin]);

  const renderFeedback = useMemo(() => {
    const hasFeedbacks = !!feedbacks?.length;

    if (!isUserAllowedToGiveFeedback && !hasFeedbacks) {
      return (
        <Typography variant="h6" color="primary.light" align="center">
          No feedback yet
        </Typography>
      );
    }

    return (
      <BoxComment>
        {feedbacks?.map((feedback) => (
          <Comment
            reactions={feedback.reactions}
            refetchCheckin={refetchCheckin}
            feedbackId={feedback.id}
            key={feedback.id}
            comment={feedback.text}
            userName={feedback.user?.name}
            date={feedback.createdAt}
          />
        ))}

        <CommentInputBox isFeedback={!hasFeedbacks} handlePostComment={handlePostFeedback} />
      </BoxComment>
    );
  }, [feedbacks, isUserAllowedToGiveFeedback, handlePostFeedback, refetchCheckin]);

  return (
    <>
      <Paper
        variant="outlined"
        sx={styles.userContainer}
        data-testid="user-container"
        key="user-container"
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={600}
            color="primary"
            sx={styles.userTextsAlign}
            data-testid="user-name"
          >
            {user?.name}
          </Typography>
          <Typography variant="subtitle1" sx={styles.userTextsAlign} data-testid="user-email">
            {user?.email}
          </Typography>
        </Box>

        <Box>
          {!!answeredAt && (
            <Typography align="right" fontSize={14} color="primary" data-testid="date-submitted">
              Submitted on <strong>{format(new Date(answeredAt), defaultCheckinDateFormat)}</strong>
            </Typography>
          )}
          {!!dueDate && (
            <Typography sx={styles.checkinDateTextsAlign} fontSize={11} variant="subtitle1">
              For the Check-In Period of{' '}
              <strong>
                <>
                  {format(new Date(dueDate.startDate), defaultCheckinDateFormat)} -{' '}
                  {format(new Date(dueDate.endDate), defaultCheckinDateFormat)}
                </>
              </strong>
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={styles.questionsContainer}
        style={{ marginBottom: '20px' }}
        data-testid="priorities-container"
        key="priorities-container"
      >
        <ContainerTitle title="Priorities" />

        {renderPriorities}
      </Paper>

      <Paper
        variant="outlined"
        sx={styles.questionsContainer}
        data-testid="questions-container"
        key="questions-container"
      >
        <ContainerTitle title="Questions" />

        {renderQuestions}
      </Paper>

      <Paper variant="outlined" sx={styles.feedbackContainer}>
        <ContainerTitle title="Feedback" />
        {renderFeedback}
      </Paper>

      {isUserAllowedToGiveFeedback && !isCheckinReviewed && (
        <Button
          onClick={handleMarkAsReviewed}
          variant="contained"
          disableElevation
          data-testid="mark-reviewed-button"
          sx={{ marginBottom: 2 }}
        >
          {reviewButtonLabel}
        </Button>
      )}
    </>
  );
};

export default UnreviewedCheckinDetails;
